<template>
  <!-- The page a client opens to approve a change order, as the app's public website draws it today
       (workspace-public-website origin/main, Oct 22 2026): pages/co/[token].vue (workspace header, the title
       card with "Change to proposal …" and Reason for the change, the decision banner, What changes,
       Totals, Payments and the response card) with components/changeOrders/ChangeOrderLineChanges.vue
       (Added / Changed / Removed tags, "1 × $1,400.00" figures), ChangeOrderTotalsLadder.vue,
       ChangeOrderPaymentImpact.vue and ChangeOrderRespond.vue (Approve change / Decline, then Your full
       name, the consent box and Approve change / Back). Every label is a literal from those files; the
       consent sentence follows the server's "I approve {ref}. The total of {doc} changes from … to …"
       (composables/changeOrders/changeOrderFixture.ts). It is a web page, not the Flutter app, so there is
       no sidebar: the window is the client's browser. The page pins the light theme (data-theme="light"),
       so this mockup stays light in dark mode too, and it keeps the page's daisyUI look and system font.
       Radius exception: the page's cards are rounded-2xl (16px) and its boxes rounded-xl (12px), copied as
       drawn. Layouts: wide and tablet = the page at sm and up (cards p-7, buttons in a row, Approve on the
       right); phone = below sm (p-5, buttons stacked), at container <= 600px. The window shows the page
       where the client is: at the response card while deciding, then scrolled to the approved banner, What
       changes and Totals once approved.
       Northstar Studio, Orchard Veterinary, the person and the proposal are sample data.
       States: review -> approve -> approved; the server renders `approved`. -->
  <div class="cow" :class="`cow--${state}`" aria-hidden="true">
    <svg class="cow-sprite" width="0" height="0">
      <symbol id="cow-lock" viewBox="0 0 24 24"><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" /></symbol>
    </svg>

    <div class="cow-win">
      <div class="cow-chrome"><span class="cow-url"><svg class="cow-i"><use href="#cow-lock" /></svg><span translate="no">369.dev/co/k7Pq2mXa</span></span></div>
      <div class="cow-vp">
        <!-- At the response card: deciding -->
        <div class="cow-view cow-view--decide" :class="{ on: !approved }">
          <div class="cow-col cow-col--end" :style="state === 'review' ? { transform: `translateY(${drop}px)` } : null">
            <section class="cow-card">
              <div class="cow-h2">What changes</div>
              <ul class="cow-lines">
                <li v-for="l in lines" :key="l.name">
                  <div class="cow-ln">
                    <div class="cow-lt"><span class="cow-kind" :class="l.kind">{{ l.label }}</span><b>{{ l.name }}</b></div>
                    <p>{{ l.desc }}</p>
                  </div>
                  <div class="cow-lr">{{ l.total }}</div>
                  <p class="cow-fig">{{ l.fig }}</p>
                </li>
              </ul>
            </section>
            <section class="cow-card">
              <div class="cow-h2 mb">Totals</div>
              <dl class="cow-ladder">
                <div><dt>Original total</dt><dd>$9,600.00</dd></div>
                <div class="rule"><dt>Total before this change</dt><dd>$9,600.00</dd></div>
                <div class="strong"><dt>This change</dt><dd>+$1,850.00</dd></div>
                <div class="rule big"><dt>New total</dt><dd>$11,450.00</dd></div>
              </dl>
            </section>
            <section class="cow-card cow-pay">
              <div class="cow-h2 mb">Payments</div>
              <dl class="cow-ladder">
                <div><dt>Paid so far</dt><dd>$3,840.00</dd></div>
                <div><dt>Balance due</dt><dd><span class="was">$5,760.00 →</span> <b>$7,610.00</b></dd></div>
              </dl>
            </section>
            <!-- The form is always laid out; in review the buttons sit over it and the column is pushed down by the
                 difference, so opening the form moves the cards with a transform, never a layout shift. -->
            <section class="cow-card cow-decide">
              <div class="cow-ask" :class="{ on: state === 'review' }">
                <div ref="askRow" class="cow-actions">
                  <span class="cow-btn pri pressed">Approve change</span>
                  <span class="cow-btn ghost">Decline</span>
                </div>
              </div>
              <div ref="form" class="cow-form" :class="{ on: state !== 'review' }">
                <label class="cow-label">Your full name</label>
                <span class="cow-input">Lena Ortiz</span>
                <span class="cow-consent"><i class="cow-check on" /><span>{{ consent }}</span></span>
                <div class="cow-actions">
                  <span class="cow-btn pri wide">Approve change</span>
                  <span class="cow-btn ghost">Back</span>
                </div>
              </div>
            </section>
          </div>
        </div>

        <!-- Approved: the banner under the title card, with What changes and Totals -->
        <div class="cow-view cow-view--top" :class="{ on: approved }">
          <div class="cow-col">
            <header class="cow-brand"><span class="cow-logo">N</span><b>Northstar Studio</b></header>
            <div class="cow-card">
              <p class="cow-ref">26-18-CO-1</p>
              <div class="cow-h1">Spring landing page and one more revision round</div>
              <p class="cow-meta">Change to proposal <b>Website refresh</b> · 26-18 · sent Oct 21, 2026</p>
              <span class="cow-link">View the proposal</span>
              <div class="cow-reason">
                <small>Reason for the change</small>
                <p>Orchard Veterinary asked for a landing page for the spring vaccination campaign and one more round of revisions on the homepage.</p>
              </div>
            </div>
            <section class="cow-decision">
              <div class="cow-dh">This change was approved</div>
              <p>Approved by Lena Ortiz. Oct 22, 2026.</p>
            </section>
            <section class="cow-card">
              <div class="cow-h2">What changes</div>
              <ul class="cow-lines">
                <li v-for="l in lines" :key="`t-${l.name}`">
                  <div class="cow-ln">
                    <div class="cow-lt"><span class="cow-kind" :class="l.kind">{{ l.label }}</span><b>{{ l.name }}</b></div>
                    <p>{{ l.desc }}</p>
                  </div>
                  <div class="cow-lr">{{ l.total }}</div>
                  <p class="cow-fig">{{ l.fig }}</p>
                </li>
              </ul>
            </section>
            <section class="cow-card">
              <div class="cow-h2 mb">Totals</div>
              <dl class="cow-ladder">
                <div><dt>Original total</dt><dd>$9,600.00</dd></div>
                <div class="rule"><dt>Total before this change</dt><dd>$9,600.00</dd></div>
                <div class="strong"><dt>This change</dt><dd>+$1,850.00</dd></div>
                <div class="rule big"><dt>New total</dt><dd>$11,450.00</dd></div>
              </dl>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  // review | approve | approved
  state: { type: String, default: "approved" },
});

const approved = computed(() => props.state === "approved");

const form = ref(null);
const askRow = ref(null);
const drop = ref(0);
let ro;
onMounted(() => {
  const measure = () => { drop.value = Math.max(0, form.value.offsetHeight - askRow.value.offsetHeight); };
  measure();
  ro = new ResizeObserver(measure);
  ro.observe(form.value);
});
onBeforeUnmount(() => ro?.disconnect());

const lines = [
  { kind: "added", label: "Added", name: "Landing page design", desc: "Spring vaccination campaign page, desktop and phone", total: "$1,400.00", fig: "1 × $1,400.00" },
  { kind: "added", label: "Added", name: "Revision round", desc: "One more round of homepage revisions", total: "$450.00", fig: "1 × $450.00" },
];
const consent = "I approve 26-18-CO-1. The total of 26-18 changes from $9,600.00 to $11,450.00.";
</script>

<style>
/* Design tokens: the public site's daisyUI 5 light theme (base-100 #fff, base-200 oklch(98% 0 0), base-content
   oklch(21% 0.006 285.885), success and error) with its slate-black primary (assets/css/tailwind.css). */
.cow {
  --b1: #fff; --b2: #f8f8f8; --bc: #18181b; --hair: rgb(24 24 27 / 0.08);
  --pri: oklch(0.279 0.006 286.033); --pri-c: oklch(0.985 0.002 247.839);
  --ok: oklch(0.76 0.177 163.223); --bad: oklch(0.71 0.194 13.428);
  position: relative;
  container-type: inline-size;
  color: var(--bc);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  line-height: 1.43;
  text-align: left;
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.cow { min-height: 640px; }
@media (max-width: 648px) { .cow { min-height: 0; aspect-ratio: 390 / 700; } }
.cow-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.cow-i { display: inline-block; flex: none; width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }

.cow-win { display: flex; flex-direction: column; height: 640px; overflow: hidden; border: 1px solid #dcdfe3; border-radius: 10px; background: var(--b2); box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12); }
.dark .cow-win { border-color: #3a3a3a; }
.cow-chrome { display: flex; flex: none; align-items: center; justify-content: center; height: 36px; border-bottom: 1px solid #e3e5e8; background: #f1f2f4; }
.cow-url { display: inline-flex; align-items: center; gap: 6px; height: 24px; min-width: 40%; justify-content: center; border-radius: 6px; background: #fff; padding: 0 12px; color: #5b6068; font-size: 12px; }
.cow-vp { position: relative; flex: 1; min-height: 0; overflow: hidden; }
.cow-view { position: absolute; inset: 0; opacity: 0; transition: opacity 0.45s ease; }
.cow-view.on { opacity: 1; }
.cow-col { display: flex; flex-direction: column; gap: 16px; width: 100%; max-width: 672px; height: 100%; margin: 0 auto; padding: 40px 24px 0; }
.cow-col--end { justify-content: flex-end; padding: 0 24px 24px; transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1); }
/* After approval the page rests scrolled so the decision banner sits just under the browser bar. */
.cow-view--top .cow-col { gap: 14px; padding: 10px 24px 0; }
.cow-view--top .cow-col > :nth-child(-n + 2) { display: none; }
.cow-brand { display: flex; flex: none; align-items: center; gap: 12px; margin-bottom: 8px; }
.cow-brand b { color: rgb(24 24 27 / 0.8); font-size: 14px; font-weight: 500; }
.cow-logo { display: grid; flex: none; place-items: center; width: 40px; height: 40px; border-radius: 8px; background: #171717; color: #fafafa; font-size: 16px; font-weight: 700; }
.cow-card { flex: none; border: 1px solid var(--hair); border-radius: 16px; background: var(--b1); padding: 28px; }
.cow-ref { margin: 0; color: rgb(24 24 27 / 0.5); font-size: 12px; font-weight: 500; letter-spacing: 0.025em; }
.cow.cow .cow-h1 { max-width: none; margin: 4px 0 0; font-size: 24px; font-weight: 600; letter-spacing: -0.015em; line-height: 1.33; }
.cow-meta { margin: 6px 0 0; color: rgb(24 24 27 / 0.6); }
.cow-meta b { color: rgb(24 24 27 / 0.8); font-weight: 500; }
.cow-link { display: inline-flex; align-items: center; min-height: 40px; margin-top: 4px; color: var(--pri); font-weight: 500; }
.cow-reason { margin-top: 12px; border-radius: 12px; background: rgb(248 248 248 / 0.6); background: color-mix(in oklab, var(--b2) 60%, transparent); padding: 12px 16px; box-shadow: inset 0 0 0 100px rgb(0 0 0 / 0.015); }
.cow-reason small { color: rgb(24 24 27 / 0.5); font-size: 12px; font-weight: 500; }
.cow-reason p { margin: 4px 0 0; line-height: 1.6; }
.cow-decision { flex: none; border: 1px solid color-mix(in oklab, var(--ok) 30%, transparent); border-radius: 16px; background: color-mix(in oklab, var(--ok) 10%, var(--b1)); padding: 16px 20px; animation: cow-in 0.5s ease 0.15s both; }
.cow.cow .cow-dh { max-width: none; margin: 0; color: inherit; font-size: 14px; font-weight: 600; letter-spacing: normal; line-height: 1.43; }
.cow-decision p { margin: 4px 0 0; color: rgb(24 24 27 / 0.7); }
/* Doubled .cow so the site's own h1/h2 type scale (.is-story section h2) never wins, and the mockup adds no page headings (the SEO audit allows one H1). */
.cow.cow .cow-h2 { max-width: none; margin: 0; color: inherit; font-size: 14px; font-weight: 600; letter-spacing: normal; line-height: 1.43; }
.cow.cow .cow-h2.mb { margin-bottom: 12px; }
.cow-lines { margin: 4px 0 0; padding: 0; list-style: none; }
.cow-lines li { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 4px 16px; padding: 14px 0; }
.cow-lines li + li { border-top: 1px solid var(--hair); }
.cow-lt { display: flex; align-items: center; gap: 8px; min-width: 0; }
.cow-lt b { overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.cow-kind { flex: none; border-radius: 6px; padding: 2px 6px; font-size: 11px; font-weight: 500; }
.cow-kind.added { background: color-mix(in oklab, var(--ok) 10%, transparent); color: color-mix(in oklab, var(--ok) 70%, #000); }
.cow-ln p { margin: 4px 0 0; color: rgb(24 24 27 / 0.6); font-size: 12px; }
.cow-lr { font-weight: 500; text-align: right; }
.cow-fig { grid-column: span 2; margin: 0; color: rgb(24 24 27 / 0.5); font-size: 12px; }
.cow-ladder { display: grid; gap: 8px; margin: 0; }
.cow-ladder div { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; }
.cow-ladder dt { color: rgb(24 24 27 / 0.6); }
.cow-ladder dd { margin: 0; }
.cow-ladder .rule { border-top: 1px solid var(--hair); padding-top: 8px; }
.cow-ladder .strong dt, .cow-ladder .strong dd { color: var(--bc); font-weight: 500; }
.cow-ladder .big { font-size: 16px; font-weight: 600; }
.cow-ladder .big dt { color: var(--bc); }
.cow-ladder .was { color: rgb(24 24 27 / 0.5); }
.cow-ladder dd b { font-weight: 500; }
.cow-actions { display: flex; flex-direction: row-reverse; justify-content: flex-start; gap: 8px; }
.cow-btn { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; border-radius: 4px; padding: 0 16px; font-size: 14px; font-weight: 600; white-space: nowrap; }
.cow-btn.pri { background: var(--pri); color: var(--pri-c); }
.cow-btn.pri.pressed { animation: cow-press 0.6s ease 0.9s both; }
.cow-btn.wide { min-width: 160px; }
.cow-btn.ghost { color: var(--bc); }
.cow-form { display: grid; gap: 16px; visibility: hidden; opacity: 0; transition: opacity 0.35s ease 0.1s, visibility 0.35s; }
.cow-form.on { visibility: visible; opacity: 1; }
.cow-decide { position: relative; }
.cow-ask { position: absolute; top: 0; right: 0; left: 0; padding: inherit; visibility: hidden; opacity: 0; transition: opacity 0.2s ease, visibility 0.2s; }
.cow-ask.on { visibility: visible; opacity: 1; }
.cow-label { margin-bottom: -12px; font-weight: 500; }
.cow-input { display: flex; align-items: center; height: 40px; border: 1px solid rgb(24 24 27 / 0.2); border-radius: 4px; padding: 0 12px; }
.cow-consent { display: flex; align-items: flex-start; gap: 12px; border: 1px solid rgb(24 24 27 / 0.1); border-radius: 12px; padding: 12px; line-height: 1.6; }
.cow-check { position: relative; flex: none; width: 20px; height: 20px; margin-top: 2px; border-radius: 4px; background: var(--pri); }
.cow-check::after { content: ""; position: absolute; top: 4px; left: 7px; width: 5px; height: 9px; border: solid var(--pri-c); border-width: 0 2px 2px 0; transform: rotate(45deg); }
@keyframes cow-in { from { opacity: 0; transform: translateY(6px); } }
@keyframes cow-press { 40% { transform: scale(0.96); } }

/* Phone: the page below sm (cards p-5, buttons stacked full width), one app point = 1/390 of the width. */
@container (max-width: 600px) {
  .cow-win { --u: calc(100cqi / 390); height: calc(var(--u) * 700); font-size: calc(var(--u) * 14); }
  .cow-chrome { height: calc(var(--u) * 34); }
  .cow-url { min-width: 70%; height: calc(var(--u) * 24); font-size: calc(var(--u) * 12); }
  .cow-col { gap: calc(var(--u) * 14); padding: calc(var(--u) * 24) calc(var(--u) * 16) 0; }
  .cow-col--end { padding: 0 calc(var(--u) * 16) calc(var(--u) * 16); }
  .cow-view--top .cow-col { padding: calc(var(--u) * 10) calc(var(--u) * 16) 0; }
  .cow-card { padding: calc(var(--u) * 20); }
  .cow.cow .cow-h1 { font-size: calc(var(--u) * 20); }
  .cow.cow .cow-h2, .cow.cow .cow-dh { font-size: calc(var(--u) * 14); }
  .cow-meta, .cow-lt b, .cow-ladder, .cow-btn, .cow-reason p, .cow-decision p, .cow-input, .cow-consent { font-size: calc(var(--u) * 14); }
  .cow-ladder .big { font-size: calc(var(--u) * 16); }
  .cow-logo { width: calc(var(--u) * 40); height: calc(var(--u) * 40); }
  .cow-actions { flex-direction: column; }
  .cow-btn.wide { min-width: 0; }
}
@media (prefers-reduced-motion: reduce) { .cow * { animation: none !important; transition: none !important; } }
</style>
